import * as React from 'react';
import styled from 'styled-components';

import { HomeWorkOutlined, PeopleOutline } from '@material-ui/icons';
import {
  convertTableBoolean,
  convertTableUserRole,
} from '../utils/convertTableValues';
import Table from '../organisms/Table';
import UsersForm from '../forms/UsersForm';
import AddContactWizard from '../forms/AddContactWizard';
import useTablePage from '../hooks/useTablePage';
import DashboardLayout from '../layouts/DashboardLayout';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import {
  createUser,
  UserTableType,
  updateUser,
  getEmployeesUsers,
} from '../api/users';
import { CLIENT_TYPES_VALUES } from '../constants/forms';
import { ModalDefaultDraggable } from '../molecules';
import ModalInfo from '../molecules/ModalInfo';
import ActionsPanelClients, {
  SelectedAdditionalParams,
} from '../organisms/ActionsPanelClients';
import UserPermitsModal from '../organisms/UserPermitsModal';
import CompanyIndividualsModal from '../organisms/CompanyIndividualsModal';

const ContactsPage = () => {
  const {
    setSearch,
    handleAdd,
    search,
    tableManuallyUpdate,
    isModalOpen,
    values,
    handleEdit,
    errors,
    createOrUpdate,
    handleCloseModal,
    handleCloseModalInfo,
    modalErrorTitle,
    modalError,
    modalTitle,
    modalSuccess,
    setModalTitle,
  } = useTablePage<UserTableType>({
    createEndpoint: createUser,
    updateEndpoint: updateUser,
    modalCreateTitle: 'Add Individual',
  });

  const modifiedGetClientsRequest = params => {
    const parameters = { ...params, user_role: 3 };

    return getEmployeesUsers(parameters).then(response => {
      return {
        ...response,
        data: {
          ...response.data,
          // Endpoint returns a bare list; the total for the pager comes in a header.
          count: Number(response.headers?.['x-total-count']) || response.data.length,
          results: response.data
            .filter(
              item =>
                (item.userRole === 3 &&
                  item.clientType === 1) ||
                (item.userRole === 3 &&
                  item.clientType === 2),
            )
            // .filter(el =>  {
            //   if(selectedPermit){
            //     const permitContactIds = selectedPermit?.permitContacts?.length > 0 && selectedPermit?.permitContacts?.map(user => user?.user)
            //     return permitContactIds?.includes(el?.id)
            //   } else {
            //     return el
            //   }
            // }
            // )
            .map(client => {
              return {
                name: [client?.firstName, client?.lastName].filter(Boolean).join(' '),
                company: client?.companyName,
                ...client,
                // isDeleted: client.isDeleted,
              };
            }),
        },
      };
    });
  };

  const [
    selectedAdditionalParams,
    setSelectedAdditionalParams,
  ] = React.useState<SelectedAdditionalParams>({
    userRole: 3,
    clientType: 1,
  });

  const [isCompanyView, setIsCompanyView] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      selectedAdditionalParams &&
      selectedAdditionalParams.clientType === 2 &&
      selectedAdditionalParams.userRole === 3
    ) {
      setIsCompanyView(true);
    } else {
      setIsCompanyView(false);
    }
  }, [selectedAdditionalParams]);

  const columnsOrder = isCompanyView
    ? ['email', 'companyName', 'phoneNumber', 'address']
    : ['name', 'email', 'company', 'companyName', 'phoneNumber', 'address'];

  const columns = React.useCallback(
    column => {
      if (
        [
          'id',
          'isDeleted',
          // 'email',
          // 'address',
          'city',
          'state',
          'street',
          'zipCode',
          'prNumber',
          'clientType',
          'licenses',
          'boLicense',
          'archEngLicense',
          'mobilePhoneNumber',
          'parentCompany',
          'tradesNames',
          'franchiseNames',
          'franchise',
          'trade',
          'avatar',
          'userRole',
          'isInvitationSent',
          'isEpsuperadmin',
          'createdAt',
          'firstName',
          'lastName',
          'contactType',
          'isAccounting',
          'isInspection',
          isCompanyView ? 'name' : 'companyName',
          isCompanyView ? 'company' : '',
        ].includes(column)
      ) {
        return null;
      }

      return tableColumnsFormatter(column);
    },
    [isCompanyView],
  );

  const formatValue = (value: any, column: string) =>
    convertTableBoolean(
      convertTableUserRole(value, column),
    );

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const [permitUserId, setPermitUserId] = React.useState<number | null>(null);
  const [showUserPermitsModal, setShowUserPermitsModal] = React.useState(false);

  const viewUserPermits = row => {
    setPermitUserId(row?.id);
    setShowUserPermitsModal(true);
  };

  const handleCloseUserPermitsModal = () => {
    setShowUserPermitsModal(false);
    setPermitUserId(null);
  };

  const [individualsCompany, setIndividualsCompany] = React.useState(null);

  const additionalActions = row => [
    {
      title: 'View Permits',
      handler: () => viewUserPermits(row),
      Icon: HomeWorkOutlined,
    },
    Number(row?.clientType) === CLIENT_TYPES_VALUES.COMPANY && {
      title: 'View Individuals',
      handler: () => setIndividualsCompany(row),
      Icon: PeopleOutline,
    },
  ].filter(Boolean);

  return (
    <DashboardLayout>
      <ActionsPanelClients
        onAdd={handleAdd}
        onSearch={setSearch}
        onSelectedAdditionalParamsChange={
          setSelectedAdditionalParams
        }
      />

      <Container>
        <Table
          customSearch
          searchQuery={search}
          columns={columns}
          rows={modifiedGetClientsRequest}
          onEdit={handleEdit}
          onDelete={null}
          columnsOrder={columnsOrder}
          formatValue={formatValue}
          manuallyUpdate={tableManuallyUpdate}
          additionalRequestParams={selectedAdditionalParams}
          additionalActions={additionalActions}
        />
      </Container>

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={
            values && (values.isDeleted || (values as any).isActive === false)
              ? Number(values.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL
                ? 'View Individual (Read Only)'
                : Number(values.clientType) === CLIENT_TYPES_VALUES.COMPANY
                ? 'View Company (Read Only)'
                : 'View User (Read Only)'
              : modalTitle
          }
          wide
        >
          {values ? (
            <UsersForm
              disabledRole
              isReadOnly={
                !!values &&
                (values.isDeleted || (values as any).isActive === false)
              }
              hideRoleAndType={
                Number(values.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL ||
                Number(values.clientType) === CLIENT_TYPES_VALUES.COMPANY
              }
              companyMode={
                Number(values.clientType) === CLIENT_TYPES_VALUES.COMPANY
              }
              updateButtonText={
                Number(values.clientType) === CLIENT_TYPES_VALUES.INDIVIDUAL
                  ? 'Update Individual'
                  : Number(values.clientType) === CLIENT_TYPES_VALUES.COMPANY
                  ? 'Update Company'
                  : 'Update User'
              }
              initialValues={values}
              modalError={modalErrorTitle.length > 0}
              initialErrors={errors}
              onSubmit={createOrUpdate}
            />
          ) : (
            <AddContactWizard
              modalError={modalErrorTitle.length > 0}
              initialErrors={errors}
              onSubmit={createOrUpdate}
              setModalTitle={setModalTitle}
            />
          )}
        </ModalDefaultDraggable>
      )}

      {showUserPermitsModal && (
        <UserPermitsModal
          userId={permitUserId}
          onClose={handleCloseUserPermitsModal}
        />
      )}

      {individualsCompany && (
        <CompanyIndividualsModal
          company={individualsCompany}
          onClose={() => setIndividualsCompany(null)}
        />
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          title="Success"
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
    </DashboardLayout>
  );
};

const Container = styled.div`
  margin: 24px 0 0 0;
  padding: 0 16px;
  box-sizing: border-box;

  @media (max-width: 840px) {
    margin: 16px 0 0 0;
    padding: 0 12px;
  }

  @media (max-width: 540px) {
    margin: 10px 0 0 0;
    padding: 0 10px;
  }
`;

export default ContactsPage;
